/**
 * The language the host site wants the widget in, after the first mount.
 *
 * init() fixes a language once; a site that switches language without a page
 * load (a single-page site, a language picker that only rewrites the DOM) had
 * no way to tell us, so some customers rebuilt the embed by hand to remount
 * the widget on every switch — and froze an old bundle name in the process.
 *
 * Two ways in, both keep the conversation where it is:
 *   - PlutoniosChat.setLanguage("en"), from the host page;
 *   - followDocument(): the embed loader asks for it unless the hotel pinned a
 *     language, and then we track `<html lang>` the way it was read at mount.
 */

type Listener = (language: string) => void;

let current: string | undefined;
const listeners = new Set<Listener>();
let observer: MutationObserver | null = null;

const normalize = (language: unknown) =>
  typeof language === "string" ? language.trim() : "";

const HostLanguage = {
  get: () => current,

  set(language: unknown) {
    const next = normalize(language);
    if (!next) {
      console.warn("PlutoniosChat.setLanguage: a language code is required, e.g. \"en\"");
      return;
    }
    if (next === current) return;
    current = next;
    listeners.forEach((listener) => {
      try {
        listener(next);
      } catch (error) {
        console.error("ChatWidget: language listener failed", error);
      }
    });
  },

  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Track `<html lang>`; idempotent, one observer per page. */
  followDocument() {
    if (observer || typeof MutationObserver === "undefined") return;
    const root = document.documentElement;
    observer = new MutationObserver(() => {
      // A site that clears the attribute mid-switch has not picked a language:
      // keep the one we have rather than falling back to English.
      const lang = normalize(root.getAttribute("lang"));
      if (lang) HostLanguage.set(lang);
    });
    observer.observe(root, { attributes: true, attributeFilter: ["lang"] });
  },

  /** Tests only. */
  reset() {
    current = undefined;
    listeners.clear();
    observer?.disconnect();
    observer = null;
  },
};

export default HostLanguage;
